import React, { useMemo, useCallback } from 'react';
import moment from 'moment';
import {
  Tooltip,
  Checkbox,
  Accordion,
  Typography,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { IMonthlyData, IMilestoneSelected } from '../types';
import { MonthContent } from './MonthContent';

import { useTranslation } from 'react-i18next';

interface IMonthAccordionProps {
  monthData: IMonthlyData;
  selectedMilestones: IMilestoneSelected[];
  expandedSubscriptions: Set<string>;
  isMilestoneAvailable: (milestone: IMilestoneSelected) => boolean;
  isMonthFullySelected: (monthMilestones: IMilestoneSelected[]) => boolean;
  onToggleMilestone: (milestone: IMilestoneSelected) => void;
  onToggleMonthMilestones: (monthMilestones: IMilestoneSelected[]) => void;
  onToggleSubscriptionExpanded: (subscriptionId: string) => void;
  inUseMilestones?: string[];
}

function getMonthName(monthData: IMonthlyData): string {
  if (!monthData?._id) return 'unknown';

  const date = moment(monthData._id, 'YYYY-MM');
  return date.isValid()
    ? date.format('MMMM YYYY').replace(/^\w/, (c) => c.toUpperCase())
    : 'unknown';
}

export function MonthAccordion({
  monthData,
  selectedMilestones,
  expandedSubscriptions,
  isMilestoneAvailable,
  isMonthFullySelected,
  onToggleMilestone,
  onToggleMonthMilestones,
  onToggleSubscriptionExpanded,
  inUseMilestones = [],
}: IMonthAccordionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const monthName = useMemo(() => getMonthName(monthData), [monthData]);
  const monthMilestones = useMemo(() => monthData.milestones || [], [monthData.milestones]);
  const isFullySelected = useMemo(
    () => isMonthFullySelected(monthMilestones),
    [isMonthFullySelected, monthMilestones]
  );
  const allInUse =
    monthMilestones.length > 0 && monthMilestones.every((m) => inUseMilestones.includes(m._id));

  const handleToggleMonth = useCallback(() => {
    onToggleMonthMilestones(monthMilestones);
  }, [onToggleMonthMilestones, monthMilestones]);

  const accordionStyles = useMemo(
    () => ({
      mb: 3,
      boxShadow: 'none',
      borderRadius: 1,
      backgroundColor: theme.palette.background.neutral,
      border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
      '&:before': { display: 'none' },
      '&.Mui-expanded': {
        backgroundColor: theme.palette.background.neutral,
      },
    }),
    [theme]
  );

  return (
    <Accordion key={monthData._id} disableGutters sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {allInUse ? (
          <Tooltip
            title={t('Some of the billing milestones are recently used in another billing order')}
            arrow
          >
            <span>
              <Checkbox
                checked={isFullySelected}
                onChange={handleToggleMonth}
                sx={{ mr: 2 }}
                disabled={allInUse}
              />
            </span>
          </Tooltip>
        ) : (
          <Checkbox
            checked={isFullySelected}
            onChange={handleToggleMonth}
            sx={{ mr: 2 }}
            disabled={allInUse}
          />
        )}
        <Typography variant="h6" fontWeight="bold">
          {monthName}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2, px: 2 }}>
        <MonthContent
          monthData={monthData}
          selectedMilestones={selectedMilestones}
          expandedSubscriptions={expandedSubscriptions}
          isMilestoneAvailable={isMilestoneAvailable}
          onToggleMilestone={onToggleMilestone}
          onToggleSubscriptionExpanded={onToggleSubscriptionExpanded}
          inUseMilestones={inUseMilestones}
        />
      </AccordionDetails>
    </Accordion>
  );
}
